import React, { useState, useEffect } from 'react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { CreditCard, Check, X, Trash2, Loader, Download } from 'lucide-react';

export default function AdminPaymentsPage() {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [filterStatus, setFilterStatus] = useState('');
  const perPage = 20;

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get(`/payments?all=1&limit=${perPage}&page=${page}${filterStatus ? `&status=${filterStatus}` : ''}`); setPayments(data.payments || []); }
    catch (e) { toast.error('Errore'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [page, filterStatus]);

  const markPaid = async (id) => {
    if (!window.confirm('Segnare come pagato e generare ricevuta?')) return;
    try { await api.post(`/payments/${id}/pay`); toast.success('Pagamento effettuato!'); load(); }
    catch (err) { toast.error(err.response?.data?.error || 'Errore'); }
  };

  const reject = async (id) => {
    if (!window.confirm('Rifiutare e ripristinare il saldo?')) return;
    try { await api.post(`/payments/${id}/reject`); toast.success('Rifiutato'); load(); }
    catch (err) { toast.error(err.response?.data?.error || 'Errore'); }
  };

  const del = async (id, status) => {
    const msg = status === 'paid'
      ? 'ATTENZIONE: Questo pagamento è già stato effettuato. Eliminandolo il saldo verrà ripristinato all\'utente. Continuare?'
      : 'Eliminare questa richiesta di pagamento? Il saldo verrà ripristinato.';
    if (!window.confirm(msg)) return;
    try { await api.delete(`/payments/admin/${id}`); toast.success('Eliminato'); load(); }
    catch (err) { toast.error(err.response?.data?.error || 'Errore'); }
  };

  const downloadReceipt = async (id) => {
    try {
      const response = await api.get(`/payments/${id}/receipt`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a'); a.href = url; a.download = `ricevuta_${id}.pdf`; a.click();
    } catch (e) { toast.error('Ricevuta non disponibile'); }
  };

  const statusColors = { requested: 'bg-yellow-600', approved: 'bg-blue-600', paid: 'bg-green-600', on_hold: 'bg-orange-600', rejected: 'bg-red-600' };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-white">Gestione Pagamenti</h1>
        <div className="flex items-center gap-3">
          <select className="input-field-dark w-40 text-sm" value={filterStatus} onChange={e => { setFilterStatus(e.target.value); setPage(1); }}>
            <option value="">Tutti gli stati</option>
            <option value="requested">Richiesti</option>
            <option value="paid">Pagati</option>
            <option value="rejected">Rifiutati</option>
          </select>
        </div>
      </div>
      {loading ? <Loader size={24} className="animate-spin text-yellow-400 mx-auto mt-20" /> : payments.length === 0 ? (
        <div className="card-dark p-12 text-center text-gray-500">Nessuna richiesta di pagamento</div>
      ) : (
        <div className="space-y-3">{payments.map(p => (
          <div key={p.id} className="card-dark p-5">
            <div className="flex items-center gap-4 mb-3">
              <CreditCard size={20} className="text-gray-500 flex-shrink-0" />
              <div className="flex-1">
                <p className="font-medium text-white">{p.first_name} {p.last_name}</p>
                <p className="text-xs text-gray-400">{p.email} · {new Date(p.requested_at).toLocaleDateString('it-IT')}{p.paid_at ? ` · Pagato: ${new Date(p.paid_at).toLocaleDateString('it-IT')}` : ''}</p>
              </div>
              <div className="text-right">
                <p className="text-xs text-gray-500">Importo: €{parseFloat(p.amount_requested).toFixed(2)}</p>
                <p className="text-xl font-bold text-white">€{parseFloat(p.net_payable).toFixed(2)}</p>
              </div>
              <span className={`text-[10px] px-2 py-1 rounded-full text-white ${statusColors[p.status]}`}>{p.status}</span>
            </div>
            {/* Dati pagamento utente */}
            <div className="bg-gray-900 rounded-lg p-3 mb-3 text-xs text-gray-400">
              <div className="flex flex-wrap gap-x-6 gap-y-1">
                {p.payment_method === 'paypal'
                  ? <span>PayPal: <strong className="text-white">{p.paypal_email || '-'}</strong></span>
                  : <span>IBAN: <strong className="text-white">{p.iban || 'Non inserito'}</strong>{p.iban_holder && <span className="ml-2">Intestato a: <strong className="text-white">{p.iban_holder}</strong></span>}</span>
                }
                {p.vat_number && <span>P.IVA: <strong className="text-white">{p.vat_number}</strong></span>}
                {p.fiscal_code && <span>C.F.: <strong className="text-white">{p.fiscal_code}</strong></span>}
              </div>
            </div>
            {/* Actions */}
            <div className="flex gap-2">
              {['requested', 'approved'].includes(p.status) && (<>
                <button onClick={() => markPaid(p.id)} className="px-4 py-2 bg-green-600 text-white text-xs rounded-lg hover:bg-green-700 inline-flex items-center gap-1"><Check size={12} />Paga</button>
                <button onClick={() => reject(p.id)} className="px-4 py-2 bg-red-600 text-white text-xs rounded-lg hover:bg-red-700 inline-flex items-center gap-1"><X size={12} />Rifiuta</button>
              </>)}
              {p.status === 'paid' && p.receipt_pdf_path && (
                <button onClick={() => downloadReceipt(p.id)} className="px-4 py-2 bg-blue-600 text-white text-xs rounded-lg hover:bg-blue-700 inline-flex items-center gap-1"><Download size={12} />Ricevuta</button>
              )}
              <button onClick={() => del(p.id, p.status)} className="px-4 py-2 bg-gray-700 text-gray-300 text-xs rounded-lg hover:bg-red-700 hover:text-white inline-flex items-center gap-1 ml-auto"><Trash2 size={12} />Elimina</button>
            </div>
          </div>
        ))}</div>
      )}
    </div>
  );
}
